import { useTranslations } from 'next-intl';
import dayjs from 'dayjs';
import { Link, ResponsiveImage } from '@/components';
import { EventCardType } from '@/modules/EventCalendarModule';
import classNames from 'classnames';
import { addToCalendar } from '@/utils';
import Pencil from '@/assets/pencil.svg';
import Calendar from '@/assets/calendar-light.svg';

interface EventCardProps {
  eventCard: EventCardType;
  className?: string;
  onClick?: (e: React.MouseEvent, data: EventCardType) => void;
}

const EventCard = ({ eventCard, className, onClick }: EventCardProps) => {
  const t = useTranslations();

  const formatDate = (date: string, format: string) => {
    return dayjs(date).format(format);
  };

  const startDate = formatDate(eventCard.eventDateTimeStart, 'MMM DD');
  const endDate = eventCard.eventDateTimeFinish
    ? (() => {
        const end = formatDate(eventCard.eventDateTimeFinish, 'MMM DD');
        return startDate !== end ? end : null;
      })()
    : null;

  const eventIsInThePast = eventCard.eventDateTimeFinish
    ? new Date(eventCard.eventDateTimeFinish) < new Date()
    : new Date(eventCard.eventDateTimeStart) < new Date();

  const displayDate = eventCard.eventDateTimeStart
    ? formatDate(eventCard.eventDateTimeStart, 'MMM DD') +
      (eventCard.eventDateTimeFinish ? ' - ' + formatDate(eventCard.eventDateTimeFinish, 'MMM DD') : '')
    : '';

  const onAddToCalendarClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    addToCalendar({
      summary: eventCard.name,
      location: eventCard.location,
      dtStart: eventCard.eventDateTimeStart,
      dtEnd: eventCard.eventDateTimeFinish,
    });
  };

  const _onClick = (e: React.MouseEvent) => {
    onClick?.(e, eventCard);
  };

  const isSquare = eventCard.imageOrientation.toLowerCase() === 'square';

  const cintent = (
    <div className="group relative flex flex-col bg-[#F5F5F5]">
      <div className="flex w-full justify-between px-2 py-2 lg:px-2.5 lg:py-2.5">
        <div className="h-fit w-auto">
          <div className="flex h-[60px] w-[60px] items-center justify-center rounded-lg border border-black/20 bg-white lg:h-[56px] lg:w-[56px]">
            {displayDate && (
              <div className="flex flex-col items-center justify-center p-1 text-center text-[15px] leading-[12px] lg:text-[14px]">
                <div>{startDate}</div>
                {endDate && <div>-</div>}
                {endDate && <div>{endDate}</div>}
              </div>
            )}
          </div>
        </div>

        <div className="w-9/12 lg:px-1">
          <div className="flex items-center justify-between text-[0.56rem] leading-[12px] font-bold">
            <span>{t('$event')}</span>
            <span className="mr-0">{eventCard.location}</span>
          </div>

          <div className="mt-1 overflow-hidden">
            <span className="line-clamp-2 text-[15px] leading-[17px] font-normal">{eventCard.name}</span>
          </div>
        </div>
      </div>

      <div className={classNames('w-full border border-black/20', isSquare ? 'aspect-[256/228]' : 'aspect-[256/153]')}>
        {eventCard.thumbnailImage && (
          <ResponsiveImage
            image={eventCard.thumbnailImage}
            widths={
              isSquare
                ? { xs: 404, sm: 356, md: 356, lg: 307, xl: 720 }
                : { xs: 404, sm: 356, md: 356, lg: 307, xl: 271 }
            }
            heights={
              isSquare
                ? { xs: 400, sm: 317, md: 317, lg: 273, xl: 641 }
                : { xs: 237, sm: 212, md: 212, lg: 183, xl: 162 }
            }
          />
        )}
      </div>

      <div className="absolute bottom-0 z-[3] flex h-1/2 w-full items-end p-1 sm:p-1.5 lg:p-2">
        <div className="flex w-full flex-wrap gap-1.5">
          {!eventCard.reportUrl &&
            eventCard.eventUrl &&
            eventCard.eventUrl.href &&
            eventCard.eventUrl.href !== '#' &&
            !eventIsInThePast && (
              <button
                className="mb-0 flex h-[36px] cursor-pointer items-center rounded-full bg-gray-100 transition-opacity group-hover:opacity-100 hover:bg-gray-200 lg:mb-1.5 pointer-fine:opacity-0"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(eventCard.eventUrl?.href, '_blank');
                }}
              >
                <span className="flex h-full items-center p-2.5">
                  <Pencil className="h-5 w-5" />
                </span>
                <span className="hidden h-full w-px bg-black/20 lg:block"></span>
                <span className="hidden px-2.5 lg:block">{t('event.eventCard.$register')}</span>
              </button>
            )}
          {!eventCard.reportUrl && !eventIsInThePast && (
            <button
              className="mb-0 flex h-[36px] cursor-pointer items-center rounded-full bg-gray-100 transition-opacity group-hover:opacity-100 hover:bg-gray-200 lg:mb-1.5 pointer-fine:opacity-0"
              onClick={onAddToCalendarClick}
            >
              <span className="flex h-full items-center p-2.5">
                <Calendar className="h-5 w-5" />
              </span>
              <span className="hidden h-full w-px bg-black/20 lg:block"></span>
              <span className="hidden px-2.5 lg:block">{t('event.eventCard.$addToCalendar')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return eventCard.reportUrl ? (
    <Link
      link={eventCard.reportUrl}
      className={classNames('block w-full cursor-pointer', className)}
      onClick={_onClick}
    >
      {cintent}
    </Link>
  ) : (
    <div className={classNames('block w-full', className)}>{cintent}</div>
  );
};

export default EventCard;
